import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  LocalOffer as OfferIcon
} from '@mui/icons-material';

const Promos = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const promoData = [
    {
      id: 'draftkings',
      sportsbook: 'DraftKings',
      title: 'Bet $5, Get $200 in Bonus Bets!',
      subtitle: 'New Users Only - Limited Time Offer',
      promoCode: 'No Code Needed',
      buttonText: 'Claim $200',
      logo: '/assets/draftkings-logo.png', // You'll need to add these logos
      backgroundColor: '#53d337',
      textColor: '#000000',
      homepageUrl: 'https://www.draftkings.com',
      disclaimer: '21+. Gambling Problem? Call 1-800-GAMBLER. New Users Only. T&Cs Apply.'
    },
    {
      id: 'fanduel',
      sportsbook: 'FanDuel',
      title: 'Bet & Get Up To $1000 Back in Bonus Bets!',
      subtitle: 'If Your First Bet Loses',
      promoCode: 'No Code Needed',
      buttonText: 'Claim Bonus',
      logo: '/assets/fanduel-logo.png',
      backgroundColor: '#1e3a8a',
      textColor: '#ffffff',
      homepageUrl: 'https://www.fanduel.com',
      disclaimer: '21+. Gambling Problem? Call 1-800-GAMBLER. New Users Only. T&Cs Apply.'
    },
    {
      id: 'caesars',
      sportsbook: 'Caesars',
      title: 'Get Up To $1000 First Bet On Caesars!',
      subtitle: 'Caesars Bonus Code',
      promoCode: 'CZRFULL',
      buttonText: 'Claim Offer',
      logo: '/assets/caesars-logo.png',
      backgroundColor: '#c8a96e',
      textColor: '#000000',
      homepageUrl: 'https://www.caesars.com/sportsbook',
      disclaimer: '21+. Gambling Problem? Call 1-877-8-HOPENY or Text HOPENY. New Users Only. T&Cs Apply.'
    },
    {
      id: 'fanatics',
      sportsbook: 'Fanatics',
      title: 'Bet $5, Get $200 in Bonus Bets - Win or Lose!',
      subtitle: 'Plus 10 Days of No Sweat SGPs!',
      promoCode: 'No Code Needed',
      buttonText: 'Get Bonus',
      logo: '/assets/fanatics-logo.png',
      backgroundColor: '#0066cc',
      textColor: '#ffffff',
      homepageUrl: 'https://www.fanaticssportsbook.com',
      disclaimer: '21+. Gambling Problem? Call 1-800-GAMBLER. New Users Only. T&Cs Apply.'
    }
  ];

  const handleClaimOffer = (promoId) => {
    const promo = promoData.find(p => p.id === promoId);
    if (promo && promo.homepageUrl) {
      window.open(promo.homepageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Section Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <OfferIcon sx={{ color: 'warning.main' }} />
        <Typography variant="h6" fontWeight="bold" sx={{ color: 'warning.main' }}>
          Special Offers
        </Typography>
      </Box>

      {/* Promo Cards */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 2,
          overflowX: isMobile ? 'visible' : 'auto',
          pb: 1
        }}
      >
        {promoData.map((promo) => (
          <Card
            key={promo.id}
            sx={{
              minWidth: isMobile ? '100%' : 320,
              maxWidth: isMobile ? '100%' : 380,
              background: `linear-gradient(135deg, ${promo.backgroundColor} 0%, ${promo.backgroundColor}dd 100%)`,
              color: promo.textColor,
              position: 'relative',
              overflow: 'visible',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[8],
              },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              {/* Sportsbook Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Avatar
                  src={promo.logo}
                  alt={promo.sportsbook}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'white',
                    border: '2px solid rgba(255,255,255,0.8)'
                  }}
                >
                  {promo.sportsbook.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {promo.sportsbook}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {promo.subtitle}
                  </Typography>
                </Box>
              </Box>

              {/* Offer Title */}
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  mb: 2,
                  lineHeight: 1.3,
                  fontSize: isMobile ? '1.1rem' : '1.25rem'
                }}
              >
                {promo.title}
              </Typography>

              {/* Promo Code Section */}
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Promo Code:
                </Typography>
                <Chip
                  label={promo.promoCode}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: promo.textColor,
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    border: `1px solid rgba(255,255,255,0.3)`
                  }}
                />
              </Box>

              {/* Claim Button */}
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleClaimOffer(promo.id)}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  color: promo.backgroundColor,
                  fontWeight: 'bold',
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: 'white',
                    transform: 'scale(1.02)'
                  },
                  boxShadow: theme.shadows[3],
                  mb: 2
                }}
              >
                {promo.buttonText}
              </Button>

              {/* Disclaimer */}
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.8,
                  display: 'block',
                  lineHeight: 1.3,
                  fontSize: '0.7rem'
                }}
              >
                {promo.disclaimer}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default Promos;
